import dayjs from 'dayjs';
import type { CalendarEvent, ScheduledAction } from '@demind-inc/core';

export const toMinutePrecision = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('YYYY-MM-DDTHH:mm');
};

export const isSameScheduleDay = (
  eventStartDate?: string | null,
  scheduleStartDate?: string | null
) => {
  if (!eventStartDate || !scheduleStartDate) {
    return false;
  }
  return dayjs(eventStartDate).isSame(scheduleStartDate, 'day');
};

export const findExistingEventForAllPlanSchedule = ({
  calendarEvents,
  taskId,
  routineId,
  scheduleType,
  scheduleStartDate,
  scheduleEndDate,
  scheduleName,
}: {
  calendarEvents: CalendarEvent[];
  taskId?: string;
  routineId?: string;
  scheduleType?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  scheduleName?: string | null;
}) => {
  const normalizedScheduleName = (scheduleName ?? '').trim().toLowerCase();

  return calendarEvents
    .filter((event) => event.status !== 'cancelled')
    .find((event) => {
      if (event.status === 'cancelled') {
        return false;
      }
      if (taskId && event.taskFrom?.taskId === taskId) {
        return true;
      }
      if (routineId && event.routineFrom?.routineActivityId === routineId) {
        return true;
      }
      if (
        scheduleType === 'sleep' &&
        event.autoScheduleFrom?.from === scheduleType &&
        isSameScheduleDay(event.end?.date, scheduleEndDate)
      ) {
        return true;
      }
      if (
        scheduleType === 'meal' &&
        event.autoScheduleFrom?.from === scheduleType &&
        normalizedScheduleName &&
        isSameScheduleDay(event.start?.date, scheduleStartDate)
      ) {
        const normalizedEventName = String(event.summary ?? '')
          .trim()
          .toLowerCase();
        return normalizedEventName.includes(normalizedScheduleName);
      }
      return false;
    });
};

export const computeTodayCalendarSignature = (
  calendarEvents: {
    start?: { date?: string };
    end?: { date?: string };
    summary?: string;
    status?: string;
    updatedDate?: string;
  }[]
): string => {
  const today = dayjs().format('YYYY-MM-DD');
  const todayEvents = calendarEvents
    .filter((e) => dayjs(e?.start?.date).isSame(today, 'day'))
    .map((e) => ({
      start: e?.start?.date ?? null,
      end: e?.end?.date ?? null,
      title: e?.summary ?? null,
      status: e?.status ?? null,
      updatedAt: e?.updatedDate ?? null,
    }))
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  return JSON.stringify({ date: today, events: todayEvents });
};

export type ReplannedScheduleMetadata = {
  beforeStartDate?: string;
  beforeEndDate?: string;
};

export const enrichSchedulesWithReplannedMetadata = (
  schedules: ScheduledAction[],
  calendarEvents: CalendarEvent[],
  resolveScheduleType: (schedule: ScheduledAction) => string
): Array<ScheduledAction & ReplannedScheduleMetadata> =>
  schedules.map((schedule) => {
    const existingEvent = findExistingEventForAllPlanSchedule({
      calendarEvents,
      taskId: schedule.todoFrom?.taskId,
      routineId: schedule.routineFrom?.routineActivityId,
      scheduleType: resolveScheduleType(schedule),
      scheduleStartDate: schedule.startDate,
      scheduleEndDate: schedule.endDate,
      scheduleName: schedule.name,
    });

    const beforeStartDate = existingEvent?.start?.date;
    const beforeEndDate = existingEvent?.end?.date;
    if (!beforeStartDate || !beforeEndDate) {
      return schedule;
    }

    const beforeStartMinute = toMinutePrecision(beforeStartDate);
    const beforeEndMinute = toMinutePrecision(beforeEndDate);
    const nextStartMinute = toMinutePrecision(schedule.startDate);
    const nextEndMinute = toMinutePrecision(schedule.endDate);

    if (beforeStartMinute === nextStartMinute && beforeEndMinute === nextEndMinute) {
      return schedule;
    }

    return {
      ...schedule,
      beforeStartDate,
      beforeEndDate,
    };
  });
